import { computed, ref, watch } from "vue";
import { v4 as uuid } from "uuid";
import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
import FilterValueDTO = App.Data.Explorer.Filter.FilterValueDTO;
import DocumentDimensionDTO = App.Data.DocumentDimensionDTO;
import DimensionDO = App.Data.DimensionDO;
import UserUploadedFileDTO = App.Data.Explorer.Filter.UserUploadedFileDTO;

/** The name the backend gives to values that were typed in instead of uploaded. */
export const INLINE_FILE_NAME = "In-line";

export const useFilterDraft = () => {
	const dimension = ref<DocumentDimensionDTO | null>(null);
	const values = ref<FilterValueDTO[]>([]);
	const valuesAreIncluded = ref(true);
	const isApplied = ref(true);
	const isQuickFilter = ref(false);

	const fileName = ref("");
	const fileText = ref("");
	const filePath = ref("");
	const fileUuid = ref(uuid());
	const originalFileText = ref("");
	const isUploadedFile = ref(false);
	const committedFileUuid = ref<string | null>(null);

	// Only a lost-customer segment filter uses this; null everywhere else.
	const cohortMonths = ref<number | null>(null);

	const sortBy = ref<string | null>(null);
	const sortDirection = ref<ActiveFilterDTO["sortDirection"]>(null);

	const mode = ref<"create" | "edit">("create");
	const baseline = ref<string>("");

	const specification = computed({
		get: (): number | null => {
			if (!dimension.value) {
				return null;
			}

			return dimension.value.type === "hierarchy"
				? dimension.value.hierarchy_definition_id ?? null
				: dimension.value.dimension_bucket_id ?? null;
		},
		set: (value: number | null) => {
			if (!dimension.value) {
				return;
			}

			dimension.value = {
				...dimension.value,
				...(dimension.value.type === "hierarchy"
					? { hierarchy_definition_id: value }
					: { dimension_bucket_id: value }),
			};
		},
	});

	const availableSpecifications = computed(() => {
		if (!dimension.value) {
			return [];
		}

		return dimension.value.type === "hierarchy"
			? dimension.value.hierarchy_definitions ?? []
			: dimension.value.dimension_buckets ?? [];
	});

	const applyDefaultSpecification = () => {
		if (!dimension.value) {
			return;
		}

		const defaults = availableSpecifications.value.find(
			(candidate: { is_default?: boolean | number }) => candidate.is_default
		);

		specification.value = defaults?.id ?? null;
	};

	const snapshot = (hierarchyValue?: FilterValueDTO | null): string =>
		JSON.stringify({
			dimensionId: dimension.value?.id ?? null,
			specification: specification.value,
			values: hierarchyValue ? [hierarchyValue] : values.value,
			valuesAreIncluded: valuesAreIncluded.value,
			isApplied: isApplied.value,
			isQuickFilter: isQuickFilter.value,
			cohortMonths: cohortMonths.value,
			fileName: fileName.value,
			fileText: fileText.value,
		});

	const markClean = (hierarchyValue?: FilterValueDTO | null) => {
		baseline.value = snapshot(hierarchyValue);
	};

	const isDirty = (hierarchyValue?: FilterValueDTO | null): boolean =>
		baseline.value !== snapshot(hierarchyValue);

	const reset = () => {
		dimension.value = null;
		values.value = [];
		valuesAreIncluded.value = true;
		isApplied.value = true;
		isQuickFilter.value = false;
		cohortMonths.value = null;
		fileName.value = "";
		fileText.value = "";
		filePath.value = "";
		fileUuid.value = uuid();
		originalFileText.value = "";
		isUploadedFile.value = false;
		committedFileUuid.value = null;
		sortBy.value = null;
		sortDirection.value = null;
		mode.value = "create";
		baseline.value = snapshot();
	};

	const loadFrom = (filter: ActiveFilterDTO, available: DimensionDO[]) => {
		const richer = available.find(
			(candidate) => candidate.id === filter.dimension.id
		);

		dimension.value = {
			...(richer ?? {}),
			...filter.dimension,
		} as DocumentDimensionDTO;

		if (specification.value === null) {
			applyDefaultSpecification();
		}

		values.value = [...(filter.values ?? [])];
		valuesAreIncluded.value = filter.valuesAreIncluded;
		isApplied.value = filter.isApplied;
		isQuickFilter.value = filter.isQuickFilter;
		sortBy.value = filter.sortBy;
		sortDirection.value = filter.sortDirection;
		cohortMonths.value = filter.dimension.cohort_no_of_months ?? null;
		mode.value = "edit";

		const file = filter.values?.[0]?.file;

		if (file) {
			fileUuid.value = file.uuid;
			fileText.value = file.data ?? "";
			originalFileText.value = file.data ?? "";
			filePath.value = file.path ?? "";
			fileName.value = file.name ?? "";
			isUploadedFile.value =
				Boolean(file.name) && file.name !== INLINE_FILE_NAME;
			committedFileUuid.value = file.uuid;
		}
	};

	/**
	 * Takes over the file the upload endpoint just stored. Its uuid is already
	 * committed server-side, so it must not be regenerated afterwards.
	 */
	const attachUploadedFile = (file: UserUploadedFileDTO) => {
		fileUuid.value = file.uuid;
		fileText.value = file.data ?? "";
		originalFileText.value = file.data ?? "";
		filePath.value = file.path;
		fileName.value = file.name ?? "";
		isUploadedFile.value = true;
		committedFileUuid.value = file.uuid;
	};

	const rekeyFile = () => {
		fileUuid.value = uuid();
	};

	/**
	 * Detaches the current file and mints a fresh uuid, so the next upload is
	 * stored under a new key instead of colliding with the file it replaces.
	 */
	const clearFile = () => {
		fileName.value = "";
		fileText.value = "";
		filePath.value = "";
		originalFileText.value = "";
		isUploadedFile.value = false;
		rekeyFile();
	};

	const setFile = (file: UserUploadedFileDTO | null) => {
		if (file) {
			attachUploadedFile(file);

			return;
		}

		clearFile();
	};

	watch(fileText, (text) => {
		if (isUploadedFile.value || fileUuid.value !== committedFileUuid.value) {
			return;
		}

		if (text !== originalFileText.value) {
			rekeyFile();
		}
	});

	const fileValue = (): FilterValueDTO[] => {
		return [
			{
				id: null,
				label: "File",
				model: null,
				level: null,
				sort_order: null,
				selection_depth: null,
				file: {
					uuid: fileUuid.value,
					path: `${fileUuid.value}.csv`,
					data: fileText.value,
					name: fileName.value,
				},
			} as FilterValueDTO,
		];
	};

	const toFilter = (
		hierarchyValue?: FilterValueDTO | null
	): ActiveFilterDTO => {
		const resolved = hierarchyValue
			? [hierarchyValue]
			: dimension.value?.type === "exception"
			? fileValue()
			: values.value;

		const resolvedDimension =
			cohortMonths.value === null
				? dimension.value
				: { ...dimension.value, cohort_no_of_months: cohortMonths.value };

		return {
			dimension: resolvedDimension as DocumentDimensionDTO,
			values: resolved,
			valuesAreIncluded: valuesAreIncluded.value,
			isApplied: isApplied.value,
			isQuickFilter: isQuickFilter.value,
			isDefault: false,

			sortBy: sortBy.value,
			sortDirection: sortDirection.value,
		};
	};

	return {
		dimension,
		values,
		valuesAreIncluded,
		isApplied,
		isQuickFilter,
		cohortMonths,
		fileName,
		fileText,
		filePath,
		fileUuid,
		isUploadedFile,
		sortBy,
		sortDirection,
		mode,
		specification,
		availableSpecifications,
		applyDefaultSpecification,
		snapshot,
		markClean,
		isDirty,
		reset,
		loadFrom,
		setFile,
		toFilter,
	};
};
